import { Injectable, inject } from '@angular/core';
import { map, type Observable } from 'rxjs';

import { HttpClientService } from './http-client.service';
import type { EmailResponseClient } from '../tokens/email-response-client.token';
import type {
  EmailResponseDto,
  EmailResponseForm,
  SubmitEmailResponsePayload,
} from '../../features/email-response/models/email-response.model';
import { EMAIL_RESPONSE } from '../../shared/constants/api-routes.constants';

@Injectable({ providedIn: 'root' })
export class EmailResponseService implements EmailResponseClient {
  private readonly http = inject(HttpClientService);

  /**
   * GET /api/email-responses/{guid}
   * Uses isResolved from response to drive confirmation state.
   */
  getEmailResponse(guid: string): Observable<EmailResponseDto> {
    return this.http.get<EmailResponseDto>(EMAIL_RESPONSE.BY_GUID(guid));
  }

  /**
   * PUT /api/email-responses/{guid}
   * Body: { response: string, emailResolutionResponseID?: number }
   */
  updateEmailResponse(
    guid: string,
    body: { response: string; emailResolutionResponseID?: number }
  ): Observable<void> {
    const { response, emailResolutionResponseID } = body;
    return this.http.put<void>(EMAIL_RESPONSE.BY_GUID(guid), {
      response,
      ...(emailResolutionResponseID != null && { emailResolutionResponseID }),
    });
  }

  getEmailResponseForm(guid: string): Observable<EmailResponseForm> {
    return this.getEmailResponse(guid).pipe(
      map((dto) => ({
        incidentNumber: guid,
        incidentId: dto.incidentId,
        medicationName: dto.normalizedMedDescription ?? '',
        employeeName: dto.employeeUsername ?? '',
        employeeId: dto.employeeId ?? '',
        hasResponded: dto.isResolved,
        respondedAt: dto.isResolved ? dto.updatedAt : null,
      }))
    );
  }

  submitEmailResponse(
    guid: string,
    payload: SubmitEmailResponsePayload
  ): Observable<void> {
    return this.updateEmailResponse(guid, {
      response: payload.comment,
      ...(payload.emailResolutionResponseID != null && {
        emailResolutionResponseID: payload.emailResolutionResponseID,
      }),
    });
  }
}

